import { MessageDescriptor } from "react-intl";
import configMessages from "../config/messages";
import serviceRequestMessages from "../../Components/messages";

export const CATEGORY_MESSAGE_PREFIX = "taskCreation.category.";

// The config sends `s_categories.key` (`ConfigBuilder#category`) while the
// messages are namespaced on the seed filename in `db/task_creation_config/`.
// Aliased, not renamed: the verbatim `hints` ids on the question rows already
// use the filename spelling.
const MESSAGE_NAMESPACE_BY_CATEGORY_KEY: Record<string, string> = {
  appliance_repair: "appliance_repairer",
  bike_repair: "bicycle_repairer",
  locksmith: "lock_repairer",
};

const namespaceFor = (categoryKey: string) =>
  MESSAGE_NAMESPACE_BY_CATEGORY_KEY[categoryKey] ?? categoryKey;

/** `undefined` for an unknown `keyName` — never throws. */
export const getCategoryMessage = (
  keyName: string
): MessageDescriptor | undefined =>
  (configMessages as Record<string, MessageDescriptor>)[
    `${CATEGORY_MESSAGE_PREFIX}${namespaceFor(keyName)}`
  ];

/**
 * s_category id → the `s_categories.key` column the `s_cat.*` display names are
 * keyed on. The tree cannot supply it: `key_name` is aliased to the FriendlyId
 * `slug` (`peintre`). By id, since a slug is regenerated from the name. DIY
 * only — the themes `db/task_creation_config/` has configs for.
 */
export const S_CATEGORY_KEY_BY_ID: Record<number, string> = {
  115: "painter",
  117: "electrician",
  118: "carpenter",
  119: "appliance_repair",
  120: "plumber",
  121: "handyman",
  132: "handyman",
  133: "locksmith",
  134: "bike_repair",
};

/**
 * An s_category's display name, by id. Not `getCategoryMessage`: that resolves
 * `taskCreation.category.*`, a plural fragment for a sentence ("painters").
 */
export const getSCategoryMessage = (
  sCategoryId: number
): MessageDescriptor | undefined => {
  const key = S_CATEGORY_KEY_BY_ID[sCategoryId];

  return key
    ? (serviceRequestMessages as Record<string, MessageDescriptor>)[
        `s_cat.${key}`
      ]
    : undefined;
};

/** `snake_case` → `camelCase`, the case `config/messages.ts`'s keys are written in. */
const camelCase = (value: string) =>
  value
    .split("_")
    .map((part, index) => (index ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join("");

const categoryNamespace = (categoryKey: string) =>
  camelCase(namespaceFor(categoryKey));

const lookup = (key: string): MessageDescriptor | undefined =>
  (configMessages as Record<string, MessageDescriptor>)[key];

/**
 * The key convention for a config question's own copy, which the config itself
 * does not carry: `taskCreation.<category>.<question>.title` and
 * `taskCreation.<category>.<question>.option.<option>`, every segment
 * camelCased. Built here rather than mapped per category, so a new category's
 * config needs only its keys added to `config/messages.ts`.
 */
export const questionTitleKey = (categoryKey: string, questionKey: string) =>
  `taskCreation.${categoryNamespace(categoryKey)}.${camelCase(
    questionKey
  )}.title`;

export const questionOptionKey = (
  categoryKey: string,
  questionKey: string,
  option: string
) =>
  `taskCreation.${categoryNamespace(categoryKey)}.${camelCase(
    questionKey
  )}.option.${camelCase(option)}`;

/**
 * A question whose title and options read the same for all 8 categories
 * declares them once, without the category segment — the question/option twin
 * of `sharedScreenTitleKey`. `desired_timing` is the first of those:
 * per-category keys would mean 40 duplicate strings to translate for the 5
 * labels it actually has.
 */
export const sharedQuestionTitleKey = (questionKey: string) =>
  `taskCreation.${camelCase(questionKey)}.title`;

export const sharedQuestionOptionKey = (questionKey: string, option: string) =>
  `taskCreation.${camelCase(questionKey)}.option.${camelCase(option)}`;

/** `undefined` when the key is not declared — never throws. */
export const getQuestionTitleMessage = (
  categoryKey: string,
  questionKey: string
) =>
  lookup(questionTitleKey(categoryKey, questionKey)) ??
  lookup(sharedQuestionTitleKey(questionKey));

export const getQuestionOptionMessage = (
  categoryKey: string,
  questionKey: string,
  option: string
) =>
  lookup(questionOptionKey(categoryKey, questionKey, option)) ??
  lookup(sharedQuestionOptionKey(questionKey, option));

/**
 * A screen's own heading and sub-heading, when it has copy of its own:
 * `taskCreation.<category>.<screen>.screenTitle` / `.screenSubtitle`. Screens
 * holding a single question have no such key — their `<h1>` is that question's
 * title — so both getters returning `undefined` is a normal case, not an error.
 */
export const screenTitleKey = (categoryKey: string, screenKey: string) =>
  `taskCreation.${categoryNamespace(categoryKey)}.${camelCase(
    screenKey
  )}.screenTitle`;

export const screenSubtitleKey = (categoryKey: string, screenKey: string) =>
  `taskCreation.${categoryNamespace(categoryKey)}.${camelCase(
    screenKey
  )}.screenSubtitle`;

/**
 * A screen whose copy is the same for all 8 categories declares it once,
 * without the category segment: `taskCreation.photos.screenTitle`. The photos
 * screen is the first of those — its heading and subtitle say nothing about
 * the trade, and the only varying part (the file cap) is interpolated.
 */
export const sharedScreenTitleKey = (screenKey: string) =>
  `taskCreation.${camelCase(screenKey)}.screenTitle`;

export const sharedScreenSubtitleKey = (screenKey: string) =>
  `taskCreation.${camelCase(screenKey)}.screenSubtitle`;

/** Category-scoped copy wins; the shared key is the fallback. */
export const getScreenTitleMessage = (categoryKey: string, screenKey: string) =>
  lookup(screenTitleKey(categoryKey, screenKey)) ??
  lookup(sharedScreenTitleKey(screenKey));

export const getScreenSubtitleMessage = (
  categoryKey: string,
  screenKey: string
) =>
  lookup(screenSubtitleKey(categoryKey, screenKey)) ??
  lookup(sharedScreenSubtitleKey(screenKey));

/**
 * A key the config carries verbatim — a `hints_intro`, a `hints` entry, a
 * `conditional_hints[].hints` entry. Unlike the title/option getters there is
 * no convention to build here: the config already holds the full id.
 */
export const getConfigMessage = (key: string) => lookup(key);
